import React, { useEffect, useState } from "react";
import { Button } from "reactstrap";
import ReactSelect from "react-select";
import styled from "styled-components";
import Modal from "components/Modal/Modal";
import Field from "components/Field/Field";
import Input from "components/Input/Input";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 18px;

  &:first-child {
    margin-top: 0px;
  }
`;

const Col = styled.div`
  flex: ${({ grow }) => grow || 1};
  min-width: 0;
`;

// O que o sensor mediu, para o operador conferir antes de registrar.
const Detected = styled.div`
  background-color: #f3f7fa;
  border: 1px solid #dbe6ee;
  border-left: 3px solid #1d84c6;
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 22px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px 24px;
`;

const DetectedItem = styled.div`
  p {
    margin: 0;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #8798ad;
  }

  span {
    font-size: 14px;
    font-weight: 500;
    color: #2e384d;
  }
`;

const FooterContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
`;

const ReasonHint = styled.p`
  font-size: 13px;
  color: #8798ad;
  margin: 0 0 18px;
`;

const REQUIRED_FIELDS = ["nfNumber", "supplierId", "inventoryItemId", "quantity"];

const formatWeight = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? `${Math.round(parsed).toLocaleString("pt-BR")} kg` : "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("pt-BR");
};

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
};

// Grau de confiança da quantidade, como o controller mediu a carga: medido (transdutor
// confiável em toda a faixa), estimado (curva treinada), parcial (parte extrapolada) e piso
// (leitura saturou — a quantidade é mínima). Ajuda o operador a decidir o quanto ajustar antes
// de lançar a nota.
const PRECISION_LABELS = {
  medido: "Medido",
  estimado: "Estimado",
  parcial: "Parcial",
  piso: "Piso (mínimo)",
};

const PRECISION_COLORS = {
  medido: "#1f8a4c",
  estimado: "#2e384d",
  parcial: "#b26a00",
  piso: "#c0392b",
};

const formatPrecision = (precision, precisionPct) => {
  const label = PRECISION_LABELS[precision] || precision || "-";
  const pct = parseFloat(precisionPct);
  return Number.isFinite(pct) ? `${label} (~${Math.round(pct)}%)` : label;
};

const toOption = (record) => ({ value: record.id, label: record.name });

const RefillEntryModal = ({
  request,
  suppliers,
  inventoryItems,
  onClose,
  onApprove,
  onReject,
}) => {
  const [discarding, setDiscarding] = useState(false);
  const [state, setState] = useState({});

  const location = (request && request.inventoryLocation) || {};
  // Um local de estoque só tem um material por vez, então o item vem do local.
  // Só cai para seleção quando o local ainda não tem movimentação.
  const locationItem = location.inventoryItem;

  useEffect(() => {
    if (!request) return;
    setDiscarding(false);
    setState({
      nfNumber: "",
      supplierId: null,
      inventoryItemId: locationItem ? locationItem.id : null,
      quantity: request.detectedQuantity,
      productValue: "",
      shipmentValue: "",
      rejectionReason: "",
    });
  }, [request, locationItem]);

  if (!request) return null;

  const onChange = (key, value) => setState(current => ({ ...current, [key]: value }));

  const itemOptions = (inventoryItems || []).map(toOption);
  const supplierOptions = (suppliers || []).map(toOption);

  const totalValue = (parseFloat(state.productValue) || 0) + (parseFloat(state.shipmentValue) || 0);

  const isInvalid = REQUIRED_FIELDS.some(key => !state[key]);

  const approve = () => onApprove(request.id, {
    quantity: parseFloat(state.quantity),
    inventoryItemId: state.inventoryItemId,
    supplierId: state.supplierId,
    nfNumber: state.nfNumber,
    productValue: parseFloat(state.productValue) || 0,
    shipmentValue: parseFloat(state.shipmentValue) || 0,
    totalValue,
  });

  const unit = (locationItem && locationItem.unit) || "kg";

  if (discarding) {
    return (
      <Modal
        isOpen
        toggleModal={onClose}
        size="md"
        title="Descartar abastecimento"
        footer={(
          <FooterContainer>
            <Button color="default" onClick={() => setDiscarding(false)}>Voltar</Button>
            <Button color="danger" onClick={() => onReject(request.id, state.rejectionReason)}>
              Confirmar descarte
            </Button>
          </FooterContainer>
        )}
      >
        <ReasonHint>
          {`Nada será lançado no estoque e o abastecimento de ${formatWeight(request.detectedQuantity)} `}
          {`no ${location.name} não aparecerá na lista de entradas de estoque. `}
          A leitura do silo no painel continua mostrando o peso real.
        </ReasonHint>
        <Row>
          <Col>
            <Field title="Motivo do descarte (opcional)" width="100%">
              <Input
                name="rejectionReason"
                style={{ minHeight: "40px" }}
                value={state.rejectionReason}
                onChange={onChange}
              />
            </Field>
          </Col>
        </Row>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      toggleModal={onClose}
      size="lg"
      title="Entrada de estoque"
      footer={(
        <FooterContainer>
          <Button color="danger" outline onClick={() => setDiscarding(true)}>Descartar</Button>
          <Button color="info" disabled={isInvalid} onClick={approve}>Registrar entrada</Button>
        </FooterContainer>
      )}
    >
      <Detected>
        <DetectedItem>
          <p>Local</p>
          <span>{location.name}</span>
        </DetectedItem>
        <DetectedItem>
          <p>Antes</p>
          <span>{formatWeight(request.startWeight)}</span>
        </DetectedItem>
        <DetectedItem>
          <p>Depois</p>
          <span>{formatWeight(request.endWeight)}</span>
        </DetectedItem>
        <DetectedItem>
          <p>Entrou</p>
          <span>{formatWeight(request.detectedQuantity)}</span>
        </DetectedItem>
        {request.precision && (
          <DetectedItem>
            <p>Precisão</p>
            <span style={{ color: PRECISION_COLORS[request.precision] }}>
              {formatPrecision(request.precision, request.precisionPct)}
            </span>
          </DetectedItem>
        )}
        <DetectedItem>
          <p>Período</p>
          <span>{`${formatDate(request.startedAt)} ${formatTime(request.startedAt)} — ${formatTime(request.endedAt)}`}</span>
        </DetectedItem>
      </Detected>

      <Row>
        <Col grow={2}>
          <Field title="Número NF" width="100%">
            <Input
              name="nfNumber"
              style={{ minHeight: "40px" }}
              maxLength={44}
              value={state.nfNumber}
              onChange={onChange}
            />
          </Field>
        </Col>
        <Col>
          <Field title="Data de competência" width="100%">
            <Input
              name="competenceDate"
              style={{ minHeight: "40px" }}
              value={formatDate(request.endedAt)}
              disabled
            />
          </Field>
        </Col>
      </Row>

      <Row>
        <Col>
          <Field title="Item" width="100%">
            {locationItem ? (
              <Input
                name="inventoryItem"
                style={{ minHeight: "40px" }}
                value={locationItem.name}
                disabled
              />
            ) : (
              <ReactSelect
                className="react-select primary"
                classNamePrefix="react-select"
                name="inventoryItemId"
                placeholder="Selecione o item"
                options={itemOptions}
                value={itemOptions.find(option => option.value === state.inventoryItemId)}
                onChange={option => onChange("inventoryItemId", option ? option.value : null)}
              />
            )}
          </Field>
        </Col>
        <Col>
          <Field title="Fornecedor" width="100%">
            <ReactSelect
              className="react-select primary"
              classNamePrefix="react-select"
              name="supplierId"
              placeholder="Selecione o fornecedor"
              options={supplierOptions}
              value={supplierOptions.find(option => option.value === state.supplierId)}
              onChange={option => onChange("supplierId", option ? option.value : null)}
            />
          </Field>
        </Col>
      </Row>

      <Row>
        <Col>
          <Field title="Quantidade" width="100%">
            <Input
              name="quantity"
              type="number"
              style={{ minHeight: "40px" }}
              value={state.quantity}
              onChange={onChange}
            />
          </Field>
        </Col>
        <Col>
          <Field title="Unidade" width="100%">
            <Input name="unit" style={{ minHeight: "40px" }} value={unit} disabled />
          </Field>
        </Col>
      </Row>

      <Row>
        <Col>
          <Field title="Local de estoque" width="100%">
            <Input
              name="inventoryLocation"
              style={{ minHeight: "40px" }}
              value={location.code ? `${location.name} (${location.code})` : location.name || ""}
              disabled
            />
          </Field>
        </Col>
      </Row>

      <Row>
        <Col>
          <Field title="Valor do produto" width="100%">
            <Input
              name="productValue"
              type="number"
              style={{ minHeight: "40px" }}
              value={state.productValue}
              onChange={onChange}
            />
          </Field>
        </Col>
        <Col>
          <Field title="Valor do frete" width="100%">
            <Input
              name="shipmentValue"
              type="number"
              style={{ minHeight: "40px" }}
              value={state.shipmentValue}
              onChange={onChange}
            />
          </Field>
        </Col>
        <Col>
          <Field title="Valor total" width="100%">
            <Input
              name="totalValue"
              style={{ minHeight: "40px" }}
              value={totalValue.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
              disabled
            />
          </Field>
        </Col>
      </Row>
    </Modal>
  );
};

export default RefillEntryModal;
